import { useState, useEffect } from 'react';
import { SpaceBetween, Container, Header, Box, Button, Textarea, Alert, Link } from '@awsui/components-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { competitiveIntelligenceApi } from '../../services/CompetitiveIntelligenceApi';
import { getUserEmail } from '../../utils/auth';
import { FileUploadPanel } from './FileUploadPanel';
import { FileClassificationPanel } from './FileClassificationPanel';

export function ContentGeneratorComponent() {
    // User input state
    const [userInput, setUserInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [userId, setUserId] = useState(null);
    
    // Get userId on mount
    useEffect(() => {
        const email = getUserEmail();
        setUserId(email);
        console.log('User email:', email);
    }, []);
    
    // Workflow state (matching Gradio pattern)
    const [workflowStage, setWorkflowStage] = useState('initial'); // initial | awaiting_clarification | awaiting_approval | running
    const [jobId, setJobId] = useState(null);
    const [brief, setBrief] = useState('');
    const [originalPrompt, setOriginalPrompt] = useState('');
    const [clarificationQuestions, setClarificationQuestions] = useState('');
    const [userResponse, setUserResponse] = useState('');
    const [progressMessages, setProgressMessages] = useState([]);
    const [finalReport, setFinalReport] = useState('');
    const [s3Url, setS3Url] = useState('');
    const [error, setError] = useState(null);
    const [showClassification, setShowClassification] = useState(false);
    const [classifications, setClassifications] = useState([]);
    
    // Resume state
    const [showResumeBanner, setShowResumeBanner] = useState(false);
    const [resumedJobInfo, setResumedJobInfo] = useState(null);
    const [showFailedBanner, setShowFailedBanner] = useState(false);
    const [failedJobInfo, setFailedJobInfo] = useState(null);

    // Check for in-progress jobs on mount
    useEffect(() => {
        const userEmail = getUserEmail();
        if (!userEmail) return;
        
        console.log('Checking for in-progress jobs for:', userEmail);
        
        const unsubscribe = competitiveIntelligenceApi.setupWebSocketHandlers({
            'jobs_list': (message) => {
                const jobs = message.jobs || [];
                console.log('Received jobs list:', jobs.length);
                
                // Find all in-progress jobs less than 3 hours old
                const THREE_HOURS_IN_SECONDS = 3 * 60 * 60;
                const currentTime = Math.floor(Date.now() / 1000);
                const inProgressJobs = jobs.filter(job => {
                    const status = job.status?.toLowerCase();
                    const progress = job.progress?.toLowerCase();
                    const jobAge = currentTime - job.created_at;
                    return (
                        (status === 'in_progress' || status === 'running') &&
                        progress !== 'complete' &&
                        jobAge < THREE_HOURS_IN_SECONDS
                    );
                });
                
                // Get most recent (highest created_at timestamp)
                const inProgressJob = inProgressJobs.length > 0
                    ? inProgressJobs.reduce((latest, job) => 
                        job.created_at > latest.created_at ? job : latest
                      )
                    : null;
                
                if (inProgressJob) {
                    console.log('Found most recent in-progress job:', inProgressJob.jobId);
                    
                    // Resume the job - reuse existing state
                    setJobId(inProgressJob.jobId);
                    setWorkflowStage('running');
                    setOriginalPrompt(inProgressJob.user_prompt);
                    setProgressMessages([
                        '🔄 Resuming research...',
                        inProgressJob.progress || 'Processing...'
                    ]);
                    setResumedJobInfo(inProgressJob);
                    setShowResumeBanner(true);
                } else {
                    // No in-progress jobs, check for most recent failed job within 24 hours
                    const TWENTY_FOUR_HOURS_IN_SECONDS = 24 * 60 * 60;
                    const failedJobs = jobs.filter(job => {
                        const jobAge = currentTime - job.created_at;
                        return job.status?.toLowerCase() === 'failed' && jobAge < TWENTY_FOUR_HOURS_IN_SECONDS;
                    });
                    
                    const failedJob = failedJobs.length > 0
                        ? failedJobs.reduce((latest, job) => 
                            job.created_at > latest.created_at ? job : latest
                          )
                        : null;
                    
                    if (failedJob) {
                        console.log('Found most recent failed job:', failedJob.jobId);
                        setFailedJobInfo(failedJob);
                        setShowFailedBanner(true);
                    }
                }
            }
        }, userEmail);
        
        // Request jobs list
        competitiveIntelligenceApi.listJobsWebSocket(userEmail);
        
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    // Setup WebSocket handlers
    useEffect(() => {
        const userEmail = getUserEmail();
        if (!userEmail) return;

        const unsubscribe = competitiveIntelligenceApi.setupWebSocketHandlers({
            'job_created': (message) => {
                console.log('Job created:', message.jobId);
                setJobId(message.jobId);
                setProgressMessages(prev => [...prev, `✅ Job created: ${message.jobId}`]);
            },
            'file_classification_needed': (message) => {
                console.log('File classification needed:', message.classifications);
                setClassifications(message.classifications);
                setShowClassification(true);
                setProgressMessages(prev => [...prev, '📄 File classification needed']);
            },
            'clarification_needed': (message) => {
                console.log('Clarification needed:', message.questions);
                setWorkflowStage('awaiting_clarification');
                setClarificationQuestions(message.questions);
                setProgressMessages(prev => [...prev, '❓ Clarification needed']);
            },
            'brief_pending': (message) => {
                console.log('Brief pending:', message.brief);
                setWorkflowStage('awaiting_approval');
                setBrief(message.brief);
                setProgressMessages(prev => [...prev, '📋 Research brief generated']);
            },
            'progress': (message) => {
                console.log('Progress:', message.message);
                setProgressMessages(prev => [...prev, message.message]);
            },
            'complete': (message) => {
                console.log('Research complete:', message.result);
                setWorkflowStage('initial');
                setFinalReport(message.result);
                setS3Url(message.s3_url || '');
                setProgressMessages(prev => [...prev, '✅ Research completed']);
            },
            'error': (message) => {
                console.error('WebSocket error:', message.message);
                setError(message.message);
                setWorkflowStage('initial');
                setProgressMessages(prev => [...prev, `❌ Error: ${message.message}`]);
            }
        }, userEmail);

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    // Handle initial research submission
    const handleStartResearch = () => {
        if (!userInput.trim()) return;
        
        const userEmail = getUserEmail();
        console.log('Starting research with user email:', userEmail);
        
        setIsSubmitting(true);
        setOriginalPrompt(userInput);
        setProgressMessages(['🔍 Starting research...']);
        setError(null);
        setFinalReport('');
        
        competitiveIntelligenceApi.startResearchWebSocket({ 
            userId: userEmail,
            prompt: userInput,
            uploaded_documents: uploadedDocuments
        });
        setUserInput('');
        setIsSubmitting(false);
    };

    // Handle clarification response
    const handleClarificationSubmit = () => {
        if (!userResponse.trim()) return;
        
        setProgressMessages(prev => [...prev, '📤 Sending clarification...']);
        setWorkflowStage('running');  // Hide clarification panel
        competitiveIntelligenceApi.sendClarificationWebSocket(jobId, userResponse);
        setUserResponse('');
    };

    // Handle brief approval
    const handleBriefApproval = (approved = true) => {
        const briefToUse = approved ? brief : brief + "\n\n## Additional Requirements\n" + userResponse;
        
        setWorkflowStage('running');
        setProgressMessages(prev => [...prev, approved ? '✅ Brief approved' : '✏️ Brief modified']);
        competitiveIntelligenceApi.approveResearchWebSocket(jobId, briefToUse, originalPrompt);
        setUserResponse('');
    };

    // Handle reset/new research
    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return 'N/A';
        const seconds = Math.floor(Date.now() / 1000 - timestamp);
        
        if (seconds < 60) return `${seconds} seconds ago`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
        return `${Math.floor(seconds / 3600)} hours ago`;
    };

    const handleNewResearch = () => {
        setWorkflowStage('initial');
        setJobId(null);
        setBrief('');
        setOriginalPrompt('');
        setClarificationQuestions('');
        setUserResponse('');
        setUserInput('');
        setProgressMessages([]);
        setFinalReport('');
        setS3Url('');
        setError(null);
    };

    return (
        <Container header={<Header variant="h1">🔬 Competitive Research Agent</Header>}>
            <SpaceBetween direction="vertical" size="l">
                {/* Resume Banner */}
                {showResumeBanner && resumedJobInfo && (
                    <Alert
                        type="info"
                        dismissible
                        onDismiss={() => setShowResumeBanner(false)}
                    >
                        You have in-progress research jobs. Please click on 'My Research' under Content tab to view status.
                    </Alert>
                )}

                {/* Failed Job Banner */}
                {showFailedBanner && failedJobInfo && (
                    <Alert
                        type="error"
                        dismissible
                        onDismiss={() => setShowFailedBanner(false)}
                    >
                        <SpaceBetween size="xs">
                            <Box>
                                <strong>Your last research failed:</strong> "{failedJobInfo.user_prompt?.substring(0, 60)}{failedJobInfo.user_prompt?.length > 60 ? '...' : ''}"
                            </Box>
                            <Box fontSize="body-s">
                                You can retry this job in My Research.
                            </Box>
                        </SpaceBetween>
                    </Alert>
                )}

                {/* Error display */}
                {error && (
                    <Alert type="error" dismissible onDismiss={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Main input area */}
                {workflowStage === 'initial' && progressMessages.length === 0 && (
                    <Container>
                        <SpaceBetween direction="vertical" size="m">
                            <Box variant="p">
                                Enter your research request below to get started. For example:
                                "Compare AWS Bedrock vs Azure OpenAI pricing" or "Create a battlecard for Amazon Q vs GitHub Copilot"
                            </Box>
                            <Textarea
                                value={userInput}
                                onChange={({ detail }) => setUserInput(detail.value)}
                                placeholder="e.g., Compare AWS Bedrock vs Azure OpenAI pricing"
                                rows={4}
                                disabled={isSubmitting}
                            />
                            <FileUploadPanel
                                userId={userId}
                                onFilesUploaded={setUploadedDocuments}
                            />
                            <Box textAlign="right">
                                <Button 
                                    variant="primary" 
                                    onClick={handleStartResearch}
                                    disabled={!userInput.trim() || isSubmitting}
                                    loading={isSubmitting}
                                >
                                    🚀 Start Research
                                </Button>
                            </Box>
                        </SpaceBetween>
                    </Container>
                )}

                {/* File Classification */}
                {showClassification && (
                    <FileClassificationPanel
                        classifications={classifications}
                        jobId={jobId}
                        onConfirm={() => setShowClassification(false)}
                    />
                )}

                {/* Progress messages */}
                {progressMessages.length > 0 && (
                    <Container header={<Header variant="h2">📊 Research Progress</Header>}>
                        <Box>
                            {progressMessages.map((msg, idx) => (
                                <div key={idx} style={{ marginBottom: '12px', paddingBottom: '8px', borderBottom: idx < progressMessages.length - 1 ? '1px solid #e1e4e8' : 'none' }}>
                                    <ReactMarkdown 
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            p: ({node, ...props}) => <p style={{margin: '0.25rem 0'}} {...props} />,
                                            code: ({node, inline, ...props}) => 
                                                inline ? (
                                                    <code style={{backgroundColor: '#f6f8fa', padding: '2px 4px', borderRadius: '3px', fontSize: '0.85em'}} {...props} />
                                                ) : (
                                                    <code style={{display: 'block', backgroundColor: '#f6f8fa', padding: '8px', borderRadius: '4px', fontSize: '0.85em'}} {...props} />
                                                ),
                                        }}
                                    >
                                        {msg}
                                    </ReactMarkdown>
                                </div>
                            ))}
                        </Box>
                    </Container>
                )}

                {/* Clarification needed */}
                {workflowStage === 'awaiting_clarification' && (
                    <Container header={<Header variant="h2">❓ Clarification Needed</Header>}>
                        <SpaceBetween direction="vertical" size="m">
                            <Box>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {clarificationQuestions}
                                </ReactMarkdown>
                            </Box>
                            <Textarea
                                value={userResponse}
                                onChange={({ detail }) => setUserResponse(detail.value)}
                                placeholder="Provide your answers here..."
                                rows={6}
                            />
                            <Box textAlign="right">
                                <Button 
                                    variant="primary" 
                                    onClick={handleClarificationSubmit}
                                    disabled={!userResponse.trim()}
                                >
                                    Submit Answers
                                </Button>
                            </Box>
                        </SpaceBetween>
                    </Container>
                )}

                {/* Brief approval */}
                {workflowStage === 'awaiting_approval' && (
                    <Container header={<Header variant="h2">📋 Research Brief</Header>}>
                        <SpaceBetween direction="vertical" size="m">
                            <Box>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {brief}
                                </ReactMarkdown>
                            </Box>
                            <Box variant="p">Type modifications below or approve to continue:</Box>
                            <Textarea
                                value={userResponse}
                                onChange={({ detail }) => setUserResponse(detail.value)}
                                placeholder="Optional: Add modifications or additional requirements..."
                                rows={4}
                            />
                            <SpaceBetween direction="horizontal" size="m">
                                <Button 
                                    variant="primary" 
                                    onClick={() => handleBriefApproval(true)}
                                >
                                    ✅ Approve & Continue
                                </Button>
                                <Button 
                                    onClick={() => handleBriefApproval(false)}
                                    disabled={!userResponse.trim()}
                                >
                                    ✏️ Approve with Modifications
                                </Button>
                            </SpaceBetween>
                        </SpaceBetween>
                    </Container>
                )}

                {/* Final report */}
                {finalReport && (
                    <Container header={<Header variant="h2">📄 Final Report</Header>}>
                        <SpaceBetween direction="vertical" size="m">
                            <Box>
                                <ReactMarkdown 
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        h1: ({node, ...props}) => <h1 style={{marginTop: '1.5rem', marginBottom: '0.5rem'}} {...props} />,
                                        h2: ({node, ...props}) => <h2 style={{marginTop: '1.25rem', marginBottom: '0.5rem'}} {...props} />,
                                        h3: ({node, ...props}) => <h3 style={{marginTop: '1rem', marginBottom: '0.5rem'}} {...props} />,
                                        p: ({node, ...props}) => <p style={{marginBottom: '0.75rem'}} {...props} />,
                                        ul: ({node, ...props}) => <ul style={{marginBottom: '0.75rem', paddingLeft: '1.5rem'}} {...props} />,
                                        ol: ({node, ...props}) => <ol style={{marginBottom: '0.75rem', paddingLeft: '1.5rem'}} {...props} />,
                                        li: ({node, ...props}) => <li style={{marginBottom: '0.25rem'}} {...props} />,
                                        table: ({node, ...props}) => (
                                            <div style={{overflowX: 'auto', marginBottom: '1rem'}}>
                                                <table style={{borderCollapse: 'collapse', width: '100%', border: '1px solid #e1e4e8'}} {...props} />
                                            </div>
                                        ),
                                        th: ({node, ...props}) => (
                                            <th style={{border: '1px solid #e1e4e8', padding: '8px 12px', backgroundColor: '#f6f8fa', textAlign: 'left'}} {...props} />
                                        ),
                                        td: ({node, ...props}) => (
                                            <td style={{border: '1px solid #e1e4e8', padding: '8px 12px'}} {...props} />
                                        ),
                                        code: ({node, inline, ...props}) => 
                                            inline ? (
                                                <code style={{backgroundColor: '#f6f8fa', padding: '2px 6px', borderRadius: '3px', fontSize: '0.9em'}} {...props} />
                                            ) : (
                                                <code style={{display: 'block', backgroundColor: '#f6f8fa', padding: '12px', borderRadius: '6px', overflowX: 'auto'}} {...props} />
                                            ),
                                        blockquote: ({node, ...props}) => (
                                            <blockquote style={{borderLeft: '4px solid #e1e4e8', paddingLeft: '1rem', margin: '1rem 0', color: '#6a737d'}} {...props} />
                                        ),
                                    }}
                                >
                                    {finalReport}
                                </ReactMarkdown>
                            </Box>
                            {s3Url && (
                                <Box>
                                    <strong>S3 Location:</strong>{' '}
                                    <a href={s3Url} target="_blank" rel="noopener noreferrer">
                                        {s3Url}
                                    </a>
                                </Box>
                            )}
                        </SpaceBetween>
                    </Container>
                )}

                {/* Action button: New Research */}
                {(progressMessages.length > 0 || finalReport) && (
                    <Box textAlign="center">
                        <Button onClick={handleNewResearch} iconName="refresh">
                            🔄 New Research
                        </Button>
                    </Box>
                )}
            </SpaceBetween>
        </Container>
    );
}